import "./ServiceStatusToggle.scss";

import { Tooltip } from "components/Tooltip/Tooltip";
import { useMemo, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useChangeServiceStatusMutation } from "services/future-medical-api";
import { SERVICE_STATUS } from "types/DataTypes";
import { useConstants } from "utils/hooks";

import { KaboodleToaster } from "../../common";

type ServiceStatusToggleProps = {
  currentService?: {
    file?: {
      services?: Array<{ id: number; status?: string }>;
    };
    service: number;
  };
  lienFileData?: {
    service: { id: number; status?: string };
  };
  documentManagementFileData?: {
    service: { id: number; status?: string };
  };
};

/*
this component displays a single toggle button that switches between "In Progress" and "Complete" status.
*/
const ServiceStatusToggle = ({
  currentService,
  lienFileData,
  documentManagementFileData,
}: ServiceStatusToggleProps) => {
  const location = useLocation();
  const { constants } = useConstants();
  const url = location.pathname;

  // simple optimistic state - just track if we're pending an update
  const [optimisticStatus, setOptimisticStatus] = useState<string | null>(null);

  const isMSAPage = url.includes("/future-medical/msa/");
  const isAmendedReviewPage = url.includes("/future-medical/amended-review");
  const isAmendedReviewScreenPage = url.includes(
    "/future-medical/ar-screening",
  );
  const isCostProjectionPage =
    url.includes("/future-medical/mcp/") ||
    url.includes("/future-medical/lcp/");
  const isLienDetailPage = url.includes("/liens/") && url.includes("/lien/");
  const isLienOverviewPage = !!url.match(/\/liens\/\d+\/overview$/);
  const isDocumentManagementPage = url.includes("/document-management");

  // find the matching service from file.services
  const matchingService = currentService?.file?.services?.find(
    (s) => s.id === currentService.service,
  );

  const [changeServiceStatus] = useChangeServiceStatusMutation();

  // convert service status values
  const serviceStatuses = useMemo(() => {
    return (
      constants?.SERVICE_STATUS?.reduce(
        (
          acc: Record<string, string>,
          status: { value: string; label: string },
        ) => {
          if (status.value) {
            acc[status.value] = status.label;
          }
          return acc;
        },
        {} as Record<string, string>,
      ) || {}
    );
  }, [constants]);

  // determine if the toggle should be shown based on the current page
  const shouldShowToggle = useMemo(() => {
    return (
      isMSAPage ||
      isAmendedReviewPage ||
      isAmendedReviewScreenPage ||
      isCostProjectionPage ||
      isLienDetailPage ||
      isLienOverviewPage ||
      isDocumentManagementPage
    );
  }, [
    isMSAPage,
    isAmendedReviewPage,
    isAmendedReviewScreenPage,
    isCostProjectionPage,
    isLienOverviewPage,
    isLienDetailPage,
    isDocumentManagementPage,
  ]);

  // get the current service status based on the page context
  const actualStatus = isLienOverviewPage
    ? lienFileData?.service?.status
    : isDocumentManagementPage
      ? documentManagementFileData?.service?.status
      : matchingService?.status;

  // use optimistic status if we have one, otherwise use actual
  const serviceStatus = optimisticStatus ?? actualStatus;

  // clear optimistic state when actual data matches our optimistic update
  useEffect(() => {
    if (optimisticStatus && actualStatus === optimisticStatus) {
      setOptimisticStatus(null);
    }
  }, [actualStatus, optimisticStatus]);

  const handleToggleStatus = async () => {
    // get the correct service ID based on the context
    // prioritize page-specific services over generic matchingService
    let serviceId = 0;
    if (isLienOverviewPage && lienFileData?.service?.id) {
      serviceId = lienFileData.service.id;
    } else if (
      isDocumentManagementPage &&
      documentManagementFileData?.service?.id
    ) {
      serviceId = documentManagementFileData.service.id;
    } else if (matchingService?.id) {
      serviceId = matchingService.id;
    }

    // toggle between in progress and completed
    const newStatus =
      actualStatus === SERVICE_STATUS.COMPLETED
        ? SERVICE_STATUS.IN_PROGRESS
        : SERVICE_STATUS.COMPLETED;

    // immediately show the new status
    setOptimisticStatus(newStatus);

    try {
      await changeServiceStatus({
        serviceId,
        status: newStatus,
      }).unwrap();

      // don't clear optimistic status here - let the useEffect handle it
      // when the actual data comes back matching our optimistic update

      KaboodleToaster.success(
        `Status set to ${serviceStatuses[newStatus].toLowerCase()}`,
      );
    } catch (error) {
      // revert to actual status on error
      setOptimisticStatus(null);

      console.error(
        `Failed to update status to ${serviceStatuses[newStatus]}:`,
        error,
      );
      KaboodleToaster.error(
        error?.data?.detail ||
          `Failed to update status to ${serviceStatuses[newStatus]}`,
      );
    }
  };

  // early returns if the toggle shouldn't be shown
  if (!shouldShowToggle) return null;
  if (
    !(currentService?.file || isLienOverviewPage || isDocumentManagementPage)
  ) {
    return null;
  }

  // determine current state for styling and tooltip text
  const isCompleted = serviceStatus === SERVICE_STATUS.COMPLETED;
  const isInProgress = serviceStatus === SERVICE_STATUS.IN_PROGRESS;

  return (
    <div className="service-status-toggle-container">
      <Tooltip
        content={
          isCompleted
            ? "Mark Service In Progress"
            : isInProgress
              ? "Mark Service Complete"
              : "Set Status"
        }
      >
        <div
          className={`vertical-toggle ${
            isCompleted ? "completed" : isInProgress ? "in-progress" : "neutral"
          }`}
          onClick={handleToggleStatus}
        >
          <div className="toggle-track">
            <div className="toggle-option top">
              <span className="material-icons">pending</span>
            </div>
            <div className="toggle-option bottom">
              <span className="material-icons">beenhere</span>
            </div>
            <div className="toggle-slider" />
          </div>
        </div>
      </Tooltip>
    </div>
  );
};

export default ServiceStatusToggle;
